import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  useBalanceHistoryPaginatedQuery,
  PAGE_LIMIT,
} from 'api/queries/balanceHistoryQueries';
import PaginationNoContentOverWarning from 'components/basic/pagination/PaginationNoContentOverWarning';
import PaginationNumbers from 'components/basic/pagination/PaginationNumbers';
import { useSelectorActiveChain } from 'components/hooks';
import MetaTags from 'components/layout/header/MetaTags';
import WalletDashboardContent from 'components/wallet/dashboard/WalletDashboardContent';
import { PAGINATION_PAGES_LIMIT } from 'config/constants';
import { useSelectPendingDepositsListIncludingFailed } from 'reduxState/balances/pendingDepositsUtils';

import BalanceHistoryDetailMobile from './BalanceHistoryDetailMobile';
import BalancesHistoryTable from './BalancesHistoryTable';
import DownloadBalanceHistoryCSV from './DownloadBalanceHistoryCSV';
import PendingBalancesTable from './PendingBalancesTable';

const SubHeading = styled.h3`
  margin: 0;
  padding: 0;
  padding-bottom: 2px;
`;

/** [Search-keyword: DepositWithdrawalPage, DepositsWithdrawalsPage] */
export default function BalancesHistoryPage() {
  const { activeChain } = useSelectorActiveChain();

  /** TxId for deposits or WithdrawalId for withdrawals */
  const [mobileDetaiId, setMobileDetailId] = useState<string | null>(null);

  const [page, setPage] = useState(1);

  const {
    data: paginatedHistoryEvents,
    isLoading,
    isPlaceholderData: isPreviousData,
  } = useBalanceHistoryPaginatedQuery(page);

  const paginatedHistoryCount = paginatedHistoryEvents?.count;

  const pendingDepositsList = useSelectPendingDepositsListIncludingFailed();

  /** Currently selected event */
  const mobileDetailEvent = useMemo(() => {
    if (!mobileDetaiId) {
      return undefined;
    }
    const selectedEvent = paginatedHistoryEvents?.items?.find(
      event =>
        event.katanaTxId === mobileDetaiId ||
        (event.type === 'withdrawal' && event.withdrawalId === mobileDetaiId)
    );
    if (selectedEvent) {
      return selectedEvent;
    }
    const selectedPendingDeposit = pendingDepositsList.find(
      deposit => deposit.sourceTxId === mobileDetaiId
    );
    return selectedPendingDeposit;
  }, [mobileDetaiId, paginatedHistoryEvents?.items, pendingDepositsList]);

  if (mobileDetailEvent) {
    return (
      <BalanceHistoryDetailMobile
        onClose={() => setMobileDetailId(null)}
        transaction={mobileDetailEvent}
        activeChain={activeChain}
      />
    );
  }

  const controlsDisabled = !paginatedHistoryCount;

  return (
    <>
      <MetaTags title="Deposits & Withdrawals" />

      <WalletDashboardContent
        title="Deposits & Withdrawals"
        showBalanceActions
        headerContentRight={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <DownloadBalanceHistoryCSV disabled={controlsDisabled} />
          </div>
        }
      >
        {!!pendingDepositsList.length && (
          <>
            <SubHeading>Pending</SubHeading>
            <PendingBalancesTable
              pendingDeposits={pendingDepositsList}
              onSelectRow={item => setMobileDetailId(item.sourceTxId)}
            />
            <SubHeading>Completed</SubHeading>
          </>
        )}
        {page > PAGINATION_PAGES_LIMIT ? (
          <PaginationNoContentOverWarning />
        ) : (
          <BalancesHistoryTable
            historyEvents={paginatedHistoryEvents?.items || []}
            initialized={paginatedHistoryCount !== undefined}
            loading={isLoading || isPreviousData}
            onSelectRow={item =>
              setMobileDetailId(
                item.type === 'withdrawal'
                  ? item.withdrawalId
                  : item.katanaTxId || null
              )
            }
          />
        )}
        {paginatedHistoryCount !== undefined && (
          <PaginationNumbers
            onSelect={setPage}
            pageLimit={PAGE_LIMIT}
            totalCount={paginatedHistoryCount}
            selected={page}
          />
        )}
      </WalletDashboardContent>
    </>
  );
}
